import { Typography, Stack } from "@mui/material";
import {
  LineChart,
  PieChart,
  pieArcLabelClasses,
  Gauge,
  gaugeClasses,
} from "@mui/x-charts";
import * as React from "react";

export function LevelFrequencyPieChart({ levelsData, width }) {
  return (
    <Stack direction={"column"}>
      <Typography
        sx={{ alignSelf: "center", fontSize: width / 15, color: "#ffe8c0" }}
      >
        Level Frequency
      </Typography>
      <PieChart
        series={[
          {
            data: levelsData.map((item, index) => ({
              ...item,
              color: [
                "#daa64b", // Dark gray
                "#9A1B1B", // Slightly lighter gray
                "#fbbc00", // Another shade
                "#A63C1A", // Deep black-gray
                "#fb5d00", // Burnt orange (accent)
              ][index % 5], // Cycle through colors
            })),
            innerRadius: width / 8,
            outerRadius: width / 2.75,
            arcLabel: (item) => Math.round(item.value * 100) / 100,
            arcLabelMinAngle: 20,
            arcLabelRadius: width / 4.5,
            cornerRadius: width / 75,
            cx: width / 2 || 150,
            cy: width / 2 || 150,
          },
        ]}
        sx={{
          [`& .${pieArcLabelClasses.root}`]: {
            fontSize: width / 25 || 12,
            fontWeight: "bold",
          },
        }}
        slotProps={{
          legend: {
            direction: "row",
            position: { vertical: "bottom", horizontal: "center" },
            itemMarkWidth: width / 10 || 20,
            itemMarkHeight: width / 30 || 10,
            markGap: width / 100 || 5,
            itemGap: width / 25 || 10,
            labelStyle: {
              fontSize: width / 25 || 12,
            },
          },
        }}
        width={width || 300}
        height={width * 1.2 || 330}
      />
    </Stack>
  );
}

export function AveragePointsPieChart({ pointsData, avgMatchPoints, width }) {
  return (
    <Stack direction={"column"}>
      <Typography
        sx={{ alignSelf: "center", fontSize: width / 15, color: "#ffe8c0" }}
      >
        Average Points
      </Typography>
      <PieChart
        series={[
          {
            data: pointsData.map((item, index) => ({
              ...item,
              color: [
                "#daa64b", // Dark gray
                "#9A1B1B", // Slightly lighter gray
                "#fbbc00", // Another shade
                "#A63C1A", // Deep black-gray
                "#fb5d00", // Burnt orange (accent)
              ][index % 5], // Cycle through colors
            })),
            innerRadius: width / 8,
            outerRadius: width / 3.5,
            arcLabel: (item) => Math.round(item.value * 100) / 100,
            arcLabelMinAngle: 20,
            arcLabelRadius: width / 5,
            cornerRadius: width / 75,
            ...pointsData,
            cx: width / 2 || 150,
            cy: width / 2 || 150,
          },
          {
            data: avgMatchPoints,
            innerRadius: width / 3.5,
            outerRadius: width / 2.75,
            cx: width / 2 || 150,
            cy: width / 2 || 150,
            arcLabel: (item) => Math.round(item.value * 100) / 100,
            arcLabelRadius: width / 3,
          },
        ]}
        sx={{
          [`& .${pieArcLabelClasses.root}`]: {
            fontSize: width / 25 || 12,
            fontWeight: "bold",
          },
        }}
        slotProps={{
          legend: {
            direction: "row",
            position: { vertical: "bottom", horizontal: "center" },
            itemMarkWidth: width / 10 || 20,
            itemMarkHeight: width / 30 || 10,
            markGap: width / 100 || 5,
            itemGap: width / 25 || 10,
            labelStyle: {
              fontSize: width / 25 || 12,
            },
          },
        }}
        width={width || 300}
        height={width * 1.2 || 330}
      />
    </Stack>
  );
}

export function EndGameFrequencyPieChart({ endgameTypeData, width }) {
  return (
    <Stack direction={"column"}>
      <Typography
        sx={{ alignSelf: "center", fontSize: width / 15, color: "#ffe8c0" }}
      >
        Endgame Frequency
      </Typography>
      <PieChart
        series={[
          {
            data: endgameTypeData.map((item, index) => ({
              ...item,
              color: [
                "#daa64b",
                "#9A1B1B",
                "#fbbc00",
                "#A63C1A",
                "#fb5d00",
                "#e7896b",
              ][index % 6],
            })),
            innerRadius: width / 8,
            outerRadius: width / 2.75,
            arcLabel: (item) => Math.round(item.value * 100) / 100,
            arcLabelMinAngle: 20,
            arcLabelRadius: width / 4.5,
            cornerRadius: width / 75,
            ...endgameTypeData,
            cx: width / 2 || 150,
            cy: width / 2 || 150,
          },
        ]}
        sx={{
          [`& .${pieArcLabelClasses.root}`]: {
            fontSize: width / 25 || 12,
            fontWeight: "bold",
          },
        }}
        slotProps={{
          legend: {
            direction: "row",
            position: { vertical: "bottom", horizontal: "center" },
            itemMarkWidth: width / 10 || 20,
            itemMarkHeight: width / 30 || 10,
            markGap: width / 100 || 5,
            itemGap: width / 25 || 10,
            labelStyle: {
              fontSize: width / 25 || 12,
            },
          },
        }}
        width={width || 300}
        height={width * 1.2 || 330}
      />
    </Stack>
  );
}

export function SpeedGauge({ averageSpeed, width }) {
  return (
    <Stack>
      <Typography
        sx={{
          alignSelf: "center",
          fontSize: width / 7,
          color: "#ffe8c0",
          textAlign: "center",
        }}
      >
        Speed
      </Typography>
      <Gauge
        width={width}
        height={width}
        value={averageSpeed}
        startAngle={-135}
        endAngle={135}
        valueMin={1}
        valueMax={5}
        cornerRadius="40%"
        sx={(theme) => ({
          alignSelf: "center",
          [`& .${gaugeClasses.valueText}`]: {
            fontSize: width / 10,
          },
          [`& .${gaugeClasses.referenceArc}`]: {
            fill: theme.palette.text.disabled,
          },
        })}
      />
    </Stack>
  );
}

export function AgilityGauge({ averageAgility, width }) {
  return (
    <Stack>
      <Typography
        sx={{
          alignSelf: "center",
          fontSize: width / 7,
          color: "#ffe8c0",
          textAlign: "center",
        }}
      >
        Agility
      </Typography>
      <Gauge
        width={width}
        height={width}
        value={averageAgility}
        startAngle={-135}
        endAngle={135}
        valueMin={1}
        valueMax={5}
        cornerRadius="40%"
        sx={(theme) => ({
          alignSelf: "center",
          [`& .${gaugeClasses.valueText}`]: {
            fontSize: width / 10,
          },
          [`& .${gaugeClasses.referenceArc}`]: {
            fill: theme.palette.text.disabled,
          },
        })}
      />
    </Stack>
  );
}

export function DefenseGauge({ averageDefense, width }) {
  return (
    <Stack>
      <Typography
        sx={{
          alignSelf: "center",
          fontSize: width / 7,
          color: "#ffe8c0",
          textAlign: "center",
        }}
      >
        Defense
      </Typography>
      <Gauge
        width={width}
        height={width}
        value={averageDefense}
        startAngle={-135}
        endAngle={135}
        valueMin={1}
        valueMax={5}
        cornerRadius="40%"
        sx={(theme) => ({
          alignSelf: "center",
          [`& .${gaugeClasses.valueText}`]: {
            fontSize: width / 10,
          },
          [`& .${gaugeClasses.referenceArc}`]: {
            fill: theme.palette.text.disabled,
          },
        })}
      />
    </Stack>
  );
}

export function ClimbingSpeedGauge({ averageClimbingSpeed, width }) {
  return (
    <Stack>
      <Typography
        sx={{
          alignSelf: "center",
          fontSize: width / 7,
          color: "#ffe8c0",
          textAlign: "center",
        }}
      >
        Climbing Speed
      </Typography>
      <Gauge
        width={width}
        height={width}
        value={averageClimbingSpeed}
        startAngle={-135}
        endAngle={135}
        valueMin={1}
        valueMax={5}
        cornerRadius="40%"
        sx={(theme) => ({
          alignSelf: "center",
          [`& .${gaugeClasses.valueText}`]: {
            fontSize: width / 10,
          },
          [`& .${gaugeClasses.referenceArc}`]: {
            fill: theme.palette.text.disabled,
          },
        })}
      />
    </Stack>
  );
}

export function MatchesLineChart({ perMatch, width }) {
  return (
    <LineChart
      xAxis={[{ data: perMatch["matches"] }]}
      series={[
        {
          data: perMatch["totalPoints"],
          label: "Total Points",
          curve: "linear",
          color: "#FFC107", // Bright Yellow
        },
        {
          data: perMatch["autoPoints"],
          label: "Auto Points",
          curve: "linear",
          color: "#FF3D00", // Bright Red
          showMark: ({ index }) => index % 1 === 0,
        },
        {
          data: perMatch["teleopPoints"],
          label: "Teleop Points",
          curve: "linear",
          color: "#FF6D00", // Fiery Orange
          showMark: ({ index }) => index % 1 === 0,
        },
        {
          data: perMatch["endgamePoints"],
          label: "Endgame Points",
          curve: "linear",
          color: "#FFEA00", // Neon Yellow
        },
      ]}
      width={width}
      height={width / 2}
    />
  );
}
